import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Snowflake, LogIn } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1618075367639-1803d6e4aa7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxzb2xpZCUyMGljZSUyMGJsb2NrJTIwbWFjcm8lMjBjb2xkfGVufDB8fHxibHVlfDE3ODYzODg2NTJ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={BG} alt="Ice" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-sky-900/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Snowflake className="h-9 w-9" />
            <span className="font-chivo font-black text-2xl tracking-tight">SFX ICE PLANT</span>
          </div>
          <p className="text-sky-100 text-lg max-w-md">
            Daily ice sales, billing and reporting for ST FRANCIS XAVIER ICE PLANT.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8 text-sky-600">
            <Snowflake className="h-8 w-8" />
            <span className="font-chivo font-black text-xl">SFX ICE PLANT</span>
          </div>
          <h1 className="font-chivo font-black text-3xl text-slate-900">Sign in</h1>
          <p className="text-slate-500 mt-1 mb-8">Enter your credentials to continue.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username" data-testid="login-username-input"
                className="h-12 mt-1.5 text-base" autoCapitalize="none"
                value={username} onChange={(e) => setUsername(e.target.value)} required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" data-testid="login-password-input"
                className="h-12 mt-1.5 text-base"
                value={password} onChange={(e) => setPassword(e.target.value)} required
              />
            </div>
          </div>

          {error && (
            <p className="text-red-600 text-sm mt-4" data-testid="login-error">{error}</p>
          )}

          <Button
            type="submit" disabled={loading} data-testid="login-submit-button"
            className="w-full h-12 mt-6 text-base font-bold bg-sky-600 hover:bg-sky-700"
          >
            <LogIn className="h-5 w-5 mr-2" />
            {loading ? "Signing in…" : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
}
